import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, CreditCard, Mail, Phone, User, School, Sparkles, AlertCircle, FileText, Copy } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "@/components/Decor";
import api from "@/lib/api";
import {
  FORMULES,
  NIVEAUX,
  ECOLES,
  CONTACT,
  BANK,
  DEPOT_GARANTIE,
  CONDITIONS_INSCRIPTION,
  TARIFS_MATIN,
  formatXPF,
} from "@/lib/data";

const STEPS = ["Parent", "Enfant", "Formule", "Récap"];

const TRANSPORT_OPTIONS = [
  { key: "2x", label: "2x / semaine", prix: 1600 },
  { key: "3x", label: "3x / semaine", prix: 2400 },
  { key: "4x", label: "4x / semaine", prix: 3200 },
  { key: "5x", label: "5x / semaine", prix: 3900 },
];

export default function InscriptionPage() {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepteConditions, setAccepteConditions] = useState(false);
  const [data, setData] = useState({
    parent: { nom: "", prenom: "", email: "", telephone: "", adresse: "" },
    enfant: { nom: "", prenom: "", date_naissance: "", ecole: "", classe: "", allergies: "", notes: "" },
    formule: { type_formule: "soutien", frequence: "3x", transport: false, transport_frequence: "3x", matin: false, matin_frequence: "3x", date_debut: "", premiere_inscription: true },
    commentaire: "",
  });

  const currentFormule = FORMULES.find((f) => f.id === data.formule.type_formule);
  const currentOption = currentFormule?.options.find((o) => o.key === data.formule.frequence);
  const transportOption = TRANSPORT_OPTIONS.find((o) => o.key === data.formule.transport_frequence);
  const matinOption = TARIFS_MATIN.find((o) => o.key === data.formule.matin_frequence);

  const isSoutien = data.formule.type_formule === "soutien";
  const showDepot = isSoutien && data.formule.premiere_inscription;
  const showAddons = isSoutien; // transport + matin addons only for soutien

  const montantEstime = useMemo(() => {
    const base = currentOption?.prix || 0;
    const transport = showAddons && data.formule.transport ? transportOption?.prix || 0 : 0;
    const matin = showAddons && data.formule.matin ? matinOption?.prix || 0 : 0;
    const depot = showDepot ? DEPOT_GARANTIE : 0;
    return base + transport + matin + depot;
  }, [currentOption, transportOption, matinOption, data.formule.transport, data.formule.matin, showDepot, showAddons]);

  const updateParent = (k, v) => setData((d) => ({ ...d, parent: { ...d.parent, [k]: v } }));
  const updateEnfant = (k, v) => setData((d) => ({ ...d, enfant: { ...d.enfant, [k]: v } }));
  const updateFormule = (k, v) => setData((d) => ({ ...d, formule: { ...d.formule, [k]: v } }));

  // Validations per step
  const canProceed = () => {
    if (step === 0) {
      const p = data.parent;
      return p.nom && p.prenom && p.email && p.telephone;
    }
    if (step === 1) {
      const e = data.enfant;
      return e.nom && e.prenom && e.ecole && e.classe;
    }
    if (step === 2) {
      const f = data.formule;
      return f.type_formule && f.frequence && (!f.transport || f.transport_frequence);
    }
    return true;
  };

  const handleNext = () => {
    if (!canProceed()) {
      toast.error("Veuillez compléter tous les champs obligatoires.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handlePrev = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    if (!accepteConditions) {
      toast.error("Veuillez accepter les conditions d'inscription pour continuer.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...data, montant_estime: montantEstime, accepte_conditions: accepteConditions };
      await api.post("/inscriptions", payload);
      setSubmitted(true);
      toast.success("Inscription envoyée avec succès !");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Une erreur est survenue. Veuillez réessayer.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copié`);
    });
  };

  if (submitted) {
    return (
      <>
        <PageHero
          eyebrow="Inscription reçue"
          title={<>Merci, c'est <em className="italic text-coral">enregistré</em> !</>}
          subtitle={`Bonjour ${data.parent.prenom}, nous avons bien reçu votre demande d'inscription. Vous recevrez une confirmation par email sous 48h.`}
        />
        <section className="pb-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl p-8 sm:p-10 border border-ocean/5 mb-6" data-testid="confirmation-card">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-full bg-green-500/10 text-green-600 flex items-center justify-center">
                  <Check size={22} />
                </div>
                <h3 className="font-display text-2xl text-ocean">Inscription confirmée</h3>
              </div>
              <p className="text-ocean/70 mb-5">Récapitulatif :</p>
              <div className="space-y-2 text-sm text-ocean/80">
                <div className="flex justify-between border-b border-ocean/5 pb-2"><span className="text-ocean/55">Parent</span><span className="font-medium">{data.parent.prenom} {data.parent.nom}</span></div>
                <div className="flex justify-between border-b border-ocean/5 pb-2"><span className="text-ocean/55">Enfant</span><span className="font-medium">{data.enfant.prenom} {data.enfant.nom} ({data.enfant.classe})</span></div>
                <div className="flex justify-between border-b border-ocean/5 pb-2"><span className="text-ocean/55">Formule</span><span className="font-medium">{currentFormule?.label} — {currentOption?.freq}</span></div>
                <div className="flex justify-between border-b border-ocean/5 pb-2"><span className="text-ocean/55">Mensualité</span><span className="font-medium">{formatXPF(currentOption?.prix || 0)}</span></div>
                {data.formule.transport && (
                  <div className="flex justify-between border-b border-ocean/5 pb-2"><span className="text-ocean/55">Transport</span><span className="font-medium">{transportOption?.label} — {formatXPF(transportOption?.prix || 0)}</span></div>
                )}
                {data.formule.matin && (
                  <div className="flex justify-between border-b border-ocean/5 pb-2"><span className="text-ocean/55">Accueil du matin</span><span className="font-medium">{matinOption?.freq.replace(" / mois", "")} — {formatXPF(matinOption?.prix || 0)}</span></div>
                )}
                {showDepot && (
                  <div className="flex justify-between border-b border-ocean/5 pb-2"><span className="text-coral font-medium">Dépôt de garantie (1ère inscription)</span><span className="font-medium text-coral">{formatXPF(DEPOT_GARANTIE)}</span></div>
                )}
                <div className="flex justify-between pt-2"><span className="text-ocean/55">Total à virer</span><span className="font-display text-xl text-coral">{formatXPF(montantEstime)}</span></div>
              </div>
            </div>

            {/* RIB COMPLET */}
            <div className="bg-ocean text-white rounded-3xl p-8 sm:p-10 mb-6">
              <div className="flex items-center gap-3 mb-5">
                <CreditCard className="text-coral" size={24} />
                <h3 className="font-display text-2xl">Paiement par virement bancaire</h3>
              </div>
              <p className="text-white/75 mb-6 text-sm leading-relaxed">
                Effectuez votre virement avec les coordonnées ci-dessous. <strong>Indiquez le nom et prénom de votre enfant en référence</strong> pour faciliter le rapprochement.
              </p>

              <div className="bg-white/10 rounded-2xl p-5 mb-5 backdrop-blur">
                <div className="text-[11px] uppercase tracking-widest text-coral mb-3 font-semibold">Informations du compte</div>
                <div className="space-y-3 text-sm">
                  <RIBRow label="Bénéficiaire" value={BANK.beneficiaire} onCopy={() => copyToClipboard(BANK.beneficiaire, "Bénéficiaire")} />
                  <RIBRow label="Banque" value={BANK.banque} />
                  <RIBRow label="IBAN" value={BANK.iban} mono onCopy={() => copyToClipboard(BANK.iban.replace(/\s/g, ""), "IBAN")} />
                  <RIBRow label="BIC / SWIFT" value={BANK.bic} mono onCopy={() => copyToClipboard(BANK.bic, "BIC")} />
                </div>
              </div>

              <div className="bg-white/10 rounded-2xl p-5 mb-5 backdrop-blur">
                <div className="text-[11px] uppercase tracking-widest text-coral mb-3 font-semibold">RIB</div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <RIBRow label="Code banque" value={BANK.code_banque} mono />
                  <RIBRow label="Code guichet" value={BANK.code_guichet} mono />
                  <RIBRow label="N° de compte" value={BANK.numero_compte} mono />
                  <RIBRow label="Clé RIB" value={BANK.cle_rib} mono />
                  <RIBRow label="Domiciliation" value={BANK.domiciliation} />
                  <RIBRow label="Référence" value="Nom + Prénom enfant" />
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-2 bg-white/10 rounded-xl p-3 hover:bg-white/15 transition-colors">
                  <Phone size={18} className="text-coral" />
                  <div>
                    <div className="text-xs text-white/55">Téléphone</div>
                    <div className="text-sm font-medium">{CONTACT.phone}</div>
                  </div>
                </a>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 bg-white/10 rounded-xl p-3 hover:bg-white/15 transition-colors">
                  <Mail size={18} className="text-coral" />
                  <div>
                    <div className="text-xs text-white/55">Email</div>
                    <div className="text-sm font-medium">{CONTACT.email}</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Prochaines étapes */}
            <div className="bg-coral/10 border border-coral/20 rounded-3xl p-7">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="text-coral" size={20} />
                <h4 className="font-display text-xl text-ocean">Documents à apporter lors de l'arrivée</h4>
              </div>
              <ul className="space-y-2 text-sm text-ocean/80">
                <li className="flex items-start gap-2"><Check size={16} className="text-coral mt-0.5 shrink-0" /> 1 photo d'identité de l'enfant</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-coral mt-0.5 shrink-0" /> 1 copie de l'assurance scolaire</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-coral mt-0.5 shrink-0" /> Justificatif de virement (le dépôt de garantie peut aussi être réglé sur place)</li>
              </ul>
            </div>

            <div className="mt-8 text-center">
              <Link to="/" className="text-coral font-medium text-sm hover:underline">Retour à l'accueil</Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Inscription"
        title={<>Inscrivez votre enfant <em className="italic text-coral">en quelques minutes</em></>}
        subtitle="Remplissez ce formulaire — nous vous contactons sous 48h pour finaliser l'inscription."
      />

      <section className="pb-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-10" data-testid="stepper">
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center gap-2">
                  <div className={`step-dot ${i === step ? "active" : i < step ? "done" : ""}`}>
                    {i < step ? <Check size={14} /> : i + 1}
                  </div>
                  <span className={`text-xs uppercase tracking-wider ${i === step ? "text-coral font-semibold" : "text-ocean/50"}`}>{label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-px mx-2 ${i < step ? "bg-ocean" : "bg-ocean/15"}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="bg-white rounded-3xl p-6 sm:p-10 border border-ocean/5 shadow-[0_20px_60px_-30px_rgba(11,39,66,0.2)]">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* STEP 0: Parent */}
                {step === 0 && (
                  <div data-testid="step-parent">
                    <div className="flex items-center gap-2 mb-6">
                      <User className="text-coral" size={20} />
                      <h2 className="font-display text-2xl text-ocean">Informations du parent</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Prénom *" value={data.parent.prenom} onChange={(v) => updateParent("prenom", v)} testid="input-parent-prenom" />
                      <Field label="Nom *" value={data.parent.nom} onChange={(v) => updateParent("nom", v)} testid="input-parent-nom" />
                      <Field label="Email *" type="email" value={data.parent.email} onChange={(v) => updateParent("email", v)} testid="input-parent-email" />
                      <Field label="Téléphone *" value={data.parent.telephone} onChange={(v) => updateParent("telephone", v)} placeholder="+689 ..." testid="input-parent-telephone" />
                      <Field label="Adresse" value={data.parent.adresse} onChange={(v) => updateParent("adresse", v)} className="sm:col-span-2" testid="input-parent-adresse" />
                    </div>
                  </div>
                )}

                {/* STEP 1: Enfant */}
                {step === 1 && (
                  <div data-testid="step-enfant">
                    <div className="flex items-center gap-2 mb-6">
                      <Sparkles className="text-coral" size={20} />
                      <h2 className="font-display text-2xl text-ocean">Informations de l'enfant</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <Field label="Prénom *" value={data.enfant.prenom} onChange={(v) => updateEnfant("prenom", v)} testid="input-enfant-prenom" />
                      <Field label="Nom *" value={data.enfant.nom} onChange={(v) => updateEnfant("nom", v)} testid="input-enfant-nom" />
                      <Field label="Date de naissance" type="date" value={data.enfant.date_naissance} onChange={(v) => updateEnfant("date_naissance", v)} testid="input-enfant-naissance" />
                      <SelectField label="Classe *" value={data.enfant.classe} onChange={(v) => updateEnfant("classe", v)} options={NIVEAUX} testid="select-enfant-classe" />
                      <SelectField
                        label="École *"
                        value={data.enfant.ecole}
                        onChange={(v) => updateEnfant("ecole", v)}
                        options={[...ECOLES.papara, ...ECOLES.mataiea, "Autre"]}
                        className="sm:col-span-2"
                        testid="select-enfant-ecole"
                      />
                      <TextAreaField label="Allergies / informations médicales" value={data.enfant.allergies} onChange={(v) => updateEnfant("allergies", v)} className="sm:col-span-2" testid="input-enfant-allergies" />
                    </div>
                  </div>
                )}

                {/* STEP 2: Formule */}
                {step === 2 && (
                  <div data-testid="step-formule">
                    <div className="flex items-center gap-2 mb-6">
                      <School className="text-coral" size={20} />
                      <h2 className="font-display text-2xl text-ocean">Choix de la formule</h2>
                    </div>

                    <div className="space-y-3 mb-6">
                      <label className="text-sm font-medium text-ocean">Type de formule *</label>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {FORMULES.map((f) => (
                          <button
                            key={f.id}
                            type="button"
                            onClick={() => {
                              updateFormule("type_formule", f.id);
                              updateFormule("frequence", f.options[0].key);
                            }}
                            data-testid={`formule-${f.id}`}
                            className={`text-left p-4 rounded-2xl border-2 transition-all ${
                              data.formule.type_formule === f.id
                                ? "border-coral bg-coral/5"
                                : "border-ocean/10 bg-white hover:border-ocean/30"
                            }`}
                          >
                            <div className="font-display text-base text-ocean mb-1">{f.label}</div>
                            <div className="text-xs text-ocean/55 leading-snug">{f.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <label className="text-sm font-medium text-ocean">Fréquence / option *</label>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {currentFormule.options.map((o) => (
                          <button
                            key={o.key}
                            type="button"
                            onClick={() => updateFormule("frequence", o.key)}
                            data-testid={`option-${o.key}`}
                            className={`flex justify-between items-center p-3 rounded-xl border-2 transition-all ${
                              data.formule.frequence === o.key
                                ? "border-coral bg-coral/5"
                                : "border-ocean/10 hover:border-ocean/30"
                            }`}
                          >
                            <span className="text-sm text-ocean">{o.freq}</span>
                            <span className="font-display text-ocean font-semibold">{formatXPF(o.prix)}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Addons (only for soutien) */}
                    {isSoutien && (
                      <div className="space-y-3 mb-2">
                        <label className="text-sm font-medium text-ocean">Options additionnelles</label>

                        {/* Transport addon */}
                        <div className="bg-lagoon/15 rounded-2xl p-5">
                          <label className="flex items-center gap-3 cursor-pointer mb-4">
                            <input
                              type="checkbox"
                              checked={data.formule.transport}
                              onChange={(e) => updateFormule("transport", e.target.checked)}
                              data-testid="checkbox-transport"
                              className="w-5 h-5 rounded accent-coral"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-ocean">🚌 Ramassage scolaire</div>
                              <div className="text-xs text-ocean/60">Nous récupérons votre enfant à la sortie des cours</div>
                            </div>
                          </label>
                          {data.formule.transport && (
                            <div className="grid sm:grid-cols-2 gap-2">
                              {TRANSPORT_OPTIONS.map((o) => (
                                <button
                                  key={o.key}
                                  type="button"
                                  onClick={() => updateFormule("transport_frequence", o.key)}
                                  data-testid={`transport-${o.key}`}
                                  className={`flex justify-between items-center p-3 rounded-xl border-2 bg-white transition-all ${
                                    data.formule.transport_frequence === o.key
                                      ? "border-coral"
                                      : "border-ocean/10 hover:border-ocean/30"
                                  }`}
                                >
                                  <span className="text-sm text-ocean">{o.label}</span>
                                  <span className="font-display text-ocean font-semibold">{formatXPF(o.prix)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Matin addon */}
                        <div className="bg-coral/10 rounded-2xl p-5">
                          <label className="flex items-center gap-3 cursor-pointer mb-4">
                            <input
                              type="checkbox"
                              checked={data.formule.matin}
                              onChange={(e) => updateFormule("matin", e.target.checked)}
                              data-testid="checkbox-matin"
                              className="w-5 h-5 rounded accent-coral"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-ocean">🌅 Accueil du matin</div>
                              <div className="text-xs text-ocean/60">Dès 4h — petit-déjeuner + dépose à l'école</div>
                            </div>
                          </label>
                          {data.formule.matin && (
                            <div className="grid sm:grid-cols-2 gap-2">
                              {TARIFS_MATIN.map((o) => (
                                <button
                                  key={o.key}
                                  type="button"
                                  onClick={() => updateFormule("matin_frequence", o.key)}
                                  data-testid={`matin-${o.key}`}
                                  className={`flex justify-between items-center p-3 rounded-xl border-2 bg-white transition-all ${
                                    data.formule.matin_frequence === o.key
                                      ? "border-coral"
                                      : "border-ocean/10 hover:border-ocean/30"
                                  }`}
                                >
                                  <span className="text-sm text-ocean">{o.freq.replace(" / mois", "")}</span>
                                  <span className="font-display text-ocean font-semibold">{formatXPF(o.prix)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Première inscription / dépôt (soutien only) */}
                    {data.formule.type_formule === "soutien" && (
                      <div className="bg-coral/10 border border-coral/20 rounded-2xl p-5 mb-2 mt-2">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={data.formule.premiere_inscription}
                            onChange={(e) => updateFormule("premiere_inscription", e.target.checked)}
                            data-testid="checkbox-premiere-inscription"
                            className="w-5 h-5 rounded accent-coral mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-ocean">C'est la première inscription de mon enfant au centre</div>
                            <div className="text-xs text-ocean/65 mt-1 leading-relaxed">
                              Un <strong>dépôt de garantie de {formatXPF(DEPOT_GARANTIE)}</strong> est demandé à la première inscription pour réserver la place.
                              Il est restitué si vous ne réinscrivez pas votre enfant l'année suivante. Décochez si votre enfant était déjà inscrit l'an dernier.
                            </div>
                          </div>
                        </label>
                      </div>
                    )}

                    <Field
                      label="Date de début souhaitée"
                      type="date"
                      value={data.formule.date_debut}
                      onChange={(v) => updateFormule("date_debut", v)}
                      className="mt-5"
                      testid="input-date-debut"
                    />
                  </div>
                )}

                {/* STEP 3: Récap */}
                {step === 3 && (
                  <div data-testid="step-recap">
                    <div className="flex items-center gap-2 mb-6">
                      <Check className="text-coral" size={20} />
                      <h2 className="font-display text-2xl text-ocean">Récapitulatif</h2>
                    </div>

                    <div className="space-y-5">
                      <RecapBlock title="Parent">
                        <RecapRow label="Nom complet" value={`${data.parent.prenom} ${data.parent.nom}`} />
                        <RecapRow label="Email" value={data.parent.email} />
                        <RecapRow label="Téléphone" value={data.parent.telephone} />
                        {data.parent.adresse && <RecapRow label="Adresse" value={data.parent.adresse} />}
                      </RecapBlock>

                      <RecapBlock title="Enfant">
                        <RecapRow label="Nom complet" value={`${data.enfant.prenom} ${data.enfant.nom}`} />
                        <RecapRow label="Classe" value={data.enfant.classe} />
                        <RecapRow label="École" value={data.enfant.ecole} />
                        {data.enfant.date_naissance && <RecapRow label="Date de naissance" value={data.enfant.date_naissance} />}
                        {data.enfant.allergies && <RecapRow label="Allergies / médical" value={data.enfant.allergies} />}
                      </RecapBlock>

                      <RecapBlock title="Formule">
                        <RecapRow label="Type" value={currentFormule.label} />
                        <RecapRow label="Option" value={currentOption.freq} />
                        {isSoutien && data.formule.transport && (
                          <RecapRow label="Transport" value={`${transportOption.label} (${formatXPF(transportOption.prix)})`} />
                        )}
                        {isSoutien && data.formule.matin && (
                          <RecapRow label="Accueil du matin" value={`${matinOption.freq.replace(" / mois", "")} (${formatXPF(matinOption.prix)})`} />
                        )}
                        {data.formule.date_debut && <RecapRow label="Début" value={data.formule.date_debut} />}
                        {showDepot && (
                          <RecapRow label="Dépôt de garantie (1ère inscription)" value={formatXPF(DEPOT_GARANTIE)} />
                        )}
                      </RecapBlock>

                      <TextAreaField
                        label="Commentaire (optionnel)"
                        value={data.commentaire}
                        onChange={(v) => setData((d) => ({ ...d, commentaire: v }))}
                        testid="input-commentaire"
                      />

                      {/* Conditions d'inscription */}
                      <div className="bg-sand rounded-2xl p-5 border border-coral/20">
                        <div className="flex items-center gap-2 mb-3">
                          <AlertCircle className="text-coral" size={18} />
                          <h4 className="font-display text-base text-ocean">Conditions d'inscription</h4>
                        </div>
                        <ol className="space-y-2 text-xs text-ocean/75 leading-relaxed pl-1 list-decimal list-inside">
                          {CONDITIONS_INSCRIPTION.map((c) => (
                            <li key={c.slice(0, 20)}>{c}</li>
                          ))}
                        </ol>
                        <label className="mt-5 flex items-start gap-3 cursor-pointer p-3 rounded-xl bg-white border border-coral/30 hover:border-coral transition-colors">
                          <input
                            type="checkbox"
                            checked={accepteConditions}
                            onChange={(e) => setAccepteConditions(e.target.checked)}
                            data-testid="checkbox-conditions"
                            className="w-5 h-5 rounded accent-coral mt-0.5"
                          />
                          <span className="text-sm text-ocean font-medium">
                            J'ai lu et j'accepte les conditions d'inscription ci-dessus.
                          </span>
                        </label>
                      </div>

                      <div className="bg-ocean text-white rounded-2xl p-5 space-y-2">
                        <div className="flex justify-between items-center text-sm border-b border-white/10 pb-2">
                          <span className="text-white/65">Mensualité {currentFormule.label.toLowerCase()}</span>
                          <span className="font-medium">{formatXPF(currentOption.prix)}</span>
                        </div>
                        {isSoutien && data.formule.transport && (
                          <div className="flex justify-between items-center text-sm border-b border-white/10 pb-2">
                            <span className="text-white/65">Transport</span>
                            <span className="font-medium">{formatXPF(transportOption.prix)}</span>
                          </div>
                        )}
                        {isSoutien && data.formule.matin && (
                          <div className="flex justify-between items-center text-sm border-b border-white/10 pb-2">
                            <span className="text-white/65">Accueil du matin</span>
                            <span className="font-medium">{formatXPF(matinOption.prix)}</span>
                          </div>
                        )}
                        {showDepot && (
                          <div className="flex justify-between items-center text-sm border-b border-white/10 pb-2">
                            <span className="text-coral">Dépôt de garantie</span>
                            <span className="font-medium text-coral">{formatXPF(DEPOT_GARANTIE)}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between pt-2">
                          <div>
                            <div className="text-xs uppercase tracking-widest text-coral mb-0.5">Total à virer</div>
                            <div className="text-[11px] text-white/55">Coordonnées bancaires affichées après validation</div>
                          </div>
                          <div className="font-display text-3xl">{formatXPF(montantEstime)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Nav buttons */}
            <div className="mt-10 flex items-center justify-between">
              <button
                type="button"
                onClick={handlePrev}
                disabled={step === 0}
                data-testid="btn-prev"
                className="btn-pill btn-ghost disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={16} /> Précédent
              </button>
              {step < STEPS.length - 1 ? (
                <button type="button" onClick={handleNext} data-testid="btn-next" className="btn-pill btn-primary">
                  Suivant <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="btn-submit-inscription"
                  className="btn-pill btn-accent disabled:opacity-60"
                >
                  {submitting ? "Envoi..." : "Valider l'inscription"} <Check size={16} />
                </button>
              )}
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-ocean/50">
            En soumettant ce formulaire, vous acceptez d'être contacté par le centre pour finaliser l'inscription.
          </p>
        </div>
      </section>
    </>
  );
}

function Field({ label, value, onChange, type = "text", className = "", placeholder = "", testid }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-wider text-ocean/55 font-semibold mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full px-4 py-3 rounded-xl border border-ocean/10 bg-sand/50 focus:bg-white focus:border-coral focus:ring-2 focus:ring-coral/20 transition-all text-sm text-ocean"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, className = "", testid }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-wider text-ocean/55 font-semibold mb-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-4 py-3 rounded-xl border border-ocean/10 bg-sand/50 focus:bg-white focus:border-coral focus:ring-2 focus:ring-coral/20 transition-all text-sm text-ocean"
      >
        <option value="">— Sélectionner —</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextAreaField({ label, value, onChange, className = "", testid }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-wider text-ocean/55 font-semibold mb-2">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        data-testid={testid}
        className="w-full px-4 py-3 rounded-xl border border-ocean/10 bg-sand/50 focus:bg-white focus:border-coral focus:ring-2 focus:ring-coral/20 transition-all text-sm text-ocean resize-none"
      />
    </div>
  );
}

function RecapBlock({ title, children }) {
  return (
    <div className="bg-sand rounded-2xl p-5">
      <div className="text-xs uppercase tracking-widest text-coral font-semibold mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function RecapRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-ocean/55">{label}</span>
      <span className="font-medium text-ocean text-right">{value}</span>
    </div>
  );
}

function RIBRow({ label, value, mono, onCopy }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-white/60 text-xs uppercase tracking-wider shrink-0">{label}</span>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`text-right truncate ${mono ? "font-mono tracking-wide" : ""} text-white`}>{value}</span>
        {onCopy && (
          <button onClick={onCopy} className="text-coral hover:text-white shrink-0 p-1 rounded hover:bg-white/10 transition-colors" title="Copier" type="button">
            <Copy size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
